/**
 * RequireUserType — route-group guard by user.type (defense-in-depth).
 *
 * RequireAuth already proves the user is authenticated + hydrated (and handles
 * the must_change_password forced redirect). This guard adds a second, purely
 * client-side belt-and-suspenders boundary so the wrong *kind* of user cannot
 * load the wrong route group by typing a URL:
 *
 *   - The (app) shell wraps its content in <RequireUserType allow="internal" />
 *     so external faculty/vendor users are bounced to the portal.
 *   - The (portal) shell wraps its content in <RequireUserType allow="external" />
 *     so internal staff are bounced to the app landing page.
 *
 * This is UX only — the backend enforces RBAC per endpoint (§9.3, §13.3). The
 * frontend never relies on hiding for security.
 *
 * If `user` is null we render the children unchanged: RequireAuth owns the
 * not-authenticated / still-loading cases and will have redirected already, so
 * this guard must not introduce its own loading/redirect race.
 */

import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuthStore } from '@/stores/auth-store';

/** Internal landing route (matches the root + post-login redirect). */
const INTERNAL_HOME = '/dashboard';
/** External (faculty/vendor) landing route. */
const PORTAL_HOME = '/portal';

/** Whether a user.type string denotes an internal staff member. */
function isInternalType(type: string | undefined): boolean {
  return type === 'internal';
}

interface RequireUserTypeProps {
  /** Which audience this route group is for. */
  allow: 'internal' | 'external';
  children: ReactNode;
}

export default function RequireUserType({ allow, children }: RequireUserTypeProps) {
  const user = useAuthStore((s) => s.user);

  // RequireAuth owns the unauthenticated / loading states; don't second-guess it.
  if (user === null) {
    return <>{children}</>;
  }

  const userIsInternal = isInternalType(user.type);

  if (allow === 'internal' && !userIsInternal) {
    return <Navigate to={PORTAL_HOME} replace />;
  }

  if (allow === 'external' && userIsInternal) {
    return <Navigate to={INTERNAL_HOME} replace />;
  }

  return <>{children}</>;
}
